import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { COUNTRIES } from "../../lib/constants";
import { parseApiError } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({
  title,
  children
}) => {
  usePartnerTranslation();
  return <section className="border-t border-[#EFE4E4] pt-5">
    <h4 className="text-sm font-extrabold uppercase tracking-wide text-[#0F1115]">
      {title}
    </h4>
    <div className="mt-3 space-y-4">{children}</div>
  </section>;
};

const Label = ({
  htmlFor,
  children
}) => {
  usePartnerTranslation();
  return <label htmlFor={htmlFor} className="text-sm font-semibold text-[#0F1115]">
    {children}
  </label>;
};

const CountrySelect = ({
  id,
  value,
  onChange,
  testid
}) => {
  usePartnerTranslation();
  return <select id={id} data-testid={testid} value={value} onChange={e => onChange(e.target.value)} className="humrahe-input mt-2 appearance-none bg-white pe-10">
    <option value="">{tr("Select a country…")}</option>
    {COUNTRIES.map(c => <option key={c} value={c}>
        {c}
      </option>)}
  </select>;
};

const TaxEditModal = ({ open, initial, onClose, onSaved }) => {
  usePartnerTranslation();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/finance/me/tax`, {
        has_vat: !!form.has_vat,
        vat_country: form.has_vat ? form.vat_country : "",
        vat_number: form.has_vat ? form.vat_number : "",
        utr_country: form.utr_country || "",
        utr_number: form.utr_number || "",
      });
      toast.success(tr("Tax details saved"));
      onSaved(res.data.tax);
    } catch (err) {
      const { message } = parseApiError(err, tr("Please try again."));
      toast.error(tr("Couldn't save tax details"), {
        description: message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="tax-edit-modal"
        className="max-h-[92vh] max-w-[520px] overflow-y-auto rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-5">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Edit tax details")}</DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">{tr("Keep this up to date so we can issue correct invoices and process payouts without delay.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 px-6 py-6">
          <Section title={tr("Value Added Tax (VAT) Number")}>
            <div>
              <Label htmlFor="vat-country">{tr("Country of issue")}</Label>
              <CountrySelect
                id="vat-country"
                testid="tax-vat-country"
                value={form.vat_country || ""}
                onChange={(v) => setForm((f) => ({ ...f, vat_country: v }))}
              />
            </div>

            <label className="flex cursor-pointer items-center gap-2 pt-2">
              <Checkbox
                data-testid="tax-has-vat"
                checked={!!form.has_vat}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, has_vat: !!v }))
                }
              />
              <span className="text-sm font-medium text-[#0F1115]">{tr("Do you have Value Added Tax (VAT) Number?")}</span>
            </label>

            {form.has_vat && (
              <div>
                <input
                  type="text"
                  data-testid="tax-vat-number"
                  value={form.vat_number || ""}
                  placeholder={tr("e.g. GB999999999")}
                  maxLength={40}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, vat_number: e.target.value }))
                  }
                  className="humrahe-input"
                />
              </div>
            )}
          </Section>

          <Section title={tr("Unique Taxpayer Reference (UTR)")}>
            <div>
              <Label htmlFor="utr-country">{tr("Country of issue")}</Label>
              <CountrySelect
                id="utr-country"
                testid="tax-utr-country"
                value={form.utr_country || ""}
                onChange={(v) => setForm((f) => ({ ...f, utr_country: v }))}
              />
            </div>
            <div>
              <input
                type="text"
                data-testid="tax-utr-number"
                value={form.utr_number || ""}
                placeholder={tr("e.g. BRCT00002570525")}
                maxLength={40}
                onChange={(e) =>
                  setForm((f) => ({ ...f, utr_number: e.target.value }))
                }
                className="humrahe-input"
              />
            </div>
          </Section>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#EFE4E4] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="tax-edit-cancel"
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
          >{tr("Cancel")}</button>
          <button
            type="button"
            data-testid="tax-edit-save"
            onClick={submit}
            disabled={saving}
            className="humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Saving…")}</>
            ) : (
              tr("Save changes")
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaxEditModal;
